import classNames from 'classnames';
import { Heading } from '@acb_devel/design-system';
import { Button } from '@acb_devel/design-system';
import { EditIcon } from '@acb_devel/design-system';
import { ProfileCard } from '@components/volunteers/atoms/ProfileCard/ProfileCard';
import { ProfileCardTitle } from '@components/volunteers/atoms/ProfileCardTitle/ProfileCardTitle';
import { ProfileCardItem } from '@components/volunteers/atoms/ProfileCardItem/ProfileCardItem';
import { ProfileCardSeparator } from '@components/volunteers/atoms/ProfileCardSeparator/ProfileCardSeparator';
import { Text } from '@acb_devel/design-system';
import { Link } from 'react-router-dom';
import { useUserContext } from '@contexts/UserContext';
import { EditAvatarModal } from '@components/volunteers/organisms/EditAvatarModal/EditAvatarModal';
import { useModal } from '@acb_devel/design-system';
import { getUserYears } from '@utils/date';

import styles from './ProfileData.module.scss';

export function ProfileData({ className }: { className?: string }) {
  const { user, loadingUser } = useUserContext();
  const { isOpen, openModal, closeModal } = useModal();

  return (
    <div className={classNames(styles.profileData, className)}>
      <div className={styles.profileData__headerContainer}>
        <Heading as="h2" variant="display-4">
          Tu perfil
        </Heading>
        <Link to="/user/edit-profile">
          <Button variant="outline" color="secondary" size="md">
            <EditIcon />
            Editar
          </Button>
        </Link>
      </div>
      <ProfileCard>
        <div className={styles.profileData__mainCardContainer}>
          <div className={styles.profileData__avatarContainer}>
            {user?.profile?.profilePictureFullUrl && (
              <img
                className={styles.profileData__avatar}
                src={user.profile.profilePictureFullUrl}
                alt="Avatar"
              />
            )}
            {!user?.profile?.profilePictureFullUrl && (
              <div className={styles.profileData__avatar} />
            )}
            <Button
              variant="solid"
              color="primary"
              isOnlyIcon
              className={styles.profileData__avatarEditButton}
              onClick={openModal}
            >
              <EditIcon width={24} height={24} />
            </Button>
            <EditAvatarModal isOpen={isOpen} closeModal={closeModal} />
          </div>
          <div className={styles.profileData__mainInfoContainer}>
            {user?.profile?.firstName && user?.profile?.lastName && (
              <Heading as="p" variant="heading-1">
                {`${user.profile.firstName} ${user.profile.lastName}`}
              </Heading>
            )}
            {user?.profile?.dateOfBirth && (
              // <Text variant="body-1">{user.profile.dateOfBirth} (-- años)</Text>
              <Text variant="body-1">
                {new Date(user.profile.dateOfBirth).toLocaleDateString(
                  'es-ES',
                  { year: 'numeric', month: 'numeric', day: 'numeric' }
                )}{' '}
                ({getUserYears(user.profile.dateOfBirth).toString()} años)
              </Text>
            )}
            {user?.profile?.city && user?.profile?.province && (
              <Text variant="body-1">
                {user.profile.city} ({user.profile.province})
              </Text>
            )}
          </div>
        </div>
      </ProfileCard>
      <ProfileCard className={styles.profileData__dataCard}>
        <ProfileCardTitle>Datos básicos</ProfileCardTitle>
        <ProfileCardItem
          title="Nombre"
          value={user?.profile?.firstName}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Apellidos"
          value={user?.profile?.lastName}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Correo electrónico"
          value={user?.emailAddress}
          loading={loadingUser}
        />
        {user?.profile?.dateOfBirth && (
          <ProfileCardItem
            title="Fecha de nacimiento"
            value={new Date(user.profile.dateOfBirth).toLocaleDateString(
              'es-ES',
              { year: 'numeric', month: 'numeric', day: 'numeric' }
            )}
            loading={loadingUser}
          />
        )}
        <ProfileCardItem
          title="Ciudad"
          value={user?.profile?.city}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Provincia"
          value={user?.profile?.province}
          loading={loadingUser}
        />
        <ProfileCardSeparator />
        <ProfileCardItem
          title="DNI"
          value={user?.profile?.identificationNumber}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Teléfono"
          value={user?.profile?.phoneNumber}
          loading={loadingUser}
        />
        <ProfileCardSeparator />
        <ProfileCardItem
          title="Skype"
          value={user?.profile?.skype}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Instragram"
          value={user?.profile?.instagram}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="X (Twitter)"
          value={user?.profile?.xTwitter}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="TikTok"
          value={user?.profile?.tiktok}
          loading={loadingUser}
        />
      </ProfileCard>
      <ProfileCard className={styles.profileData__dataCard}>
        <ProfileCardTitle>Experiencia</ProfileCardTitle>
        <ProfileCardItem
          title="Ocupación actual"
          value={user?.profile?.occupation}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Formación"
          value={user?.profile?.education}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Experiencia eventos deportivos"
          value={user?.profile?.previousExperience}
          loading={loadingUser}
        />
        <ProfileCardSeparator />
        <ProfileCardItem
          title="Nivel de inglés"
          value={user?.profile?.englishLevel}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Motivos para ser voluntario"
          value={user?.profile?.motivation}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Relación con el baloncesto"
          value={user?.profile?.relationWithBasketball}
          loading={loadingUser}
        />
      </ProfileCard>
      <ProfileCard className={styles.profileData__dataCard}>
        <ProfileCardTitle>Preferencias</ProfileCardTitle>
        <ProfileCardItem
          title="Talla polo y sudadera"
          value={user?.profile?.shirtSize?.toUpperCase()}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Talla pantalón"
          value={user?.profile?.pantsSize?.toUpperCase()}
          loading={loadingUser}
        />
        <ProfileCardItem
          title="Acepta recibir información"
          value={user?.profile?.informAboutFutureEvents ? 'Sí' : 'No'}
          loading={loadingUser}
        />
      </ProfileCard>
    </div>
  );
}
